import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useParams, Link } from 'react-router-dom';
import FileUpload from '../components/FileUpload';
import ImagePreview from '../components/ImagePreview';
import DatePickerField from '../components/DatePickerField';
import { fetchWithAuth } from '../services/api';
import { getCdnUrl } from '../utils/cdnConfig';
import { getLanguageFromPath, hasValidAuthentication, saveIntendedDestination } from '../services/auth';
import { trackVisit } from '../services/visitTrackingService';
import './ProfileCreator.css';

const ProfileCreator = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { profileId } = useParams(); // Get profileId from URL params for edit mode
  const isEditMode = !!profileId; // Determine if we're in edit mode
  
  const [userData, setUserData] = useState(null);
  const [quotaExceeded, setQuotaExceeded] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(isEditMode);
  const [formData, setFormData] = useState({
    name: '',
    introduction: '',
    lifeStory: '',
    birthDate: '',
    birthPlace: '',
    deathDate: '',
    deathPlace: '',
    restingPlaceName: '',
    restingPlaceCity: '',
    restingPlaceCountry: '',
    coordinates: '',
    defaultLanguage: '',
    isAllowed: true, // Admin-only field to control profile visibility
    gallery: []
  });

  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [previewUrls, setPreviewUrls] = useState({
    gallery: []
  });
  const [creationSuccess, setCreationSuccess] = useState(null);
  const [createdProfileId, setCreatedProfileId] = useState(null);
  
  // Track existing images and their deletion status
  const [existingImages, setExistingImages] = useState([]);
  const [imagesToDelete, setImagesToDelete] = useState([]);

  // Track which image should be main during creation (index-based for new images)
  const [localMainImageIndex, setLocalMainImageIndex] = useState(0);
  
  // QR Code selection states
  const [showQrSelection, setShowQrSelection] = useState(false);
  const [availableQrCodes, setAvailableQrCodes] = useState([]);
  const [selectedQrCode, setSelectedQrCode] = useState('');
  const [isAssigningQr, setIsAssigningQr] = useState(false);
  const [currentQrCode, setCurrentQrCode] = useState(null);
  const [showQrChangeModal, setShowQrChangeModal] = useState(false);
  const [allUserQrCodes, setAllUserQrCodes] = useState([]);
  const [pendingProfileData, setPendingProfileData] = useState(null);
  const [isWaitingForQrSelection, setIsWaitingForQrSelection] = useState(false);
  
  // Authorization states
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const [authError, setAuthError] = useState(null);
  
  // Store the numeric profile ID for QR code matching
  const [numericProfileId, setNumericProfileId] = useState(null);

  // GPS location modal state
  const [showGpsModal, setShowGpsModal] = useState(false);
  const [gpsStatus, setGpsStatus] = useState('idle'); // 'idle' | 'fetching' | 'success' | 'error' | 'not_supported'

  useEffect(() => {
    if (!isEditMode) {
      trackVisit('CREATE_PROFILE');
    }
  }, [isEditMode]);

  const fetchUserData = async () => {
    try {
      const response = await fetchWithAuth('/users/me');
      setUserData(response);
      
      // Set user's default language for new profiles
      if (!isEditMode && response.defaultLanguage) {
        setFormData(prevData => ({
          ...prevData,
          defaultLanguage: response.defaultLanguage
        }));
      }
      
      // Check if user has reached their profile limit (only for create mode)
      if (!isEditMode && response.profiles && response.profiles.length >= response.maxProfiles) {
        setQuotaExceeded(true);
      }
    } catch (error) {
      console.error('Failed to fetch user data:', error);
    }
  };

  const fetchUnusedQrCodes = async () => {
    try {
      const qrCodes = await fetchWithAuth('/qrcodes/my-unused');
      setAvailableQrCodes(qrCodes);
      return qrCodes;
    } catch (error) {
      console.error('Failed to fetch unused QR codes:', error);
      return [];
    }
  };

  const fetchAllUserQrCodes = async () => {
    try {
      const qrCodes = await fetchWithAuth('/qrcodes/my-qrcodes');
      console.log('Fetched QR codes from API:', qrCodes.length, 'codes');
      setAllUserQrCodes(qrCodes);
      return qrCodes;
    } catch (error) {
      console.error('Failed to fetch all user QR codes:', error);
      setAllUserQrCodes([]);
      return [];
    }
  };

  const handleQrCodeSelection = async (profileId) => {
    // Fetch user's unused QR codes
    const qrCodes = await fetchUnusedQrCodes();
    
    if (qrCodes.length === 0) {
      // No QR codes available - show success now
      console.log('No unused QR codes available for assignment');
      setCreationSuccess(t('profileCreator.success.created'));
      return;
    } else if (qrCodes.length === 1) {
      // Auto-assign the single QR code
      try {
        await fetchWithAuth('/qrcodes/assign-to-profile', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            qrCode: qrCodes[0].code,
            profileId: profileId
          })
        });
        console.log('Auto-assigned single QR code:', qrCodes[0].code);
        setCreationSuccess(t('profileCreator.success.created') + ' ' + t('profileCreator.qrCode.autoAssigned', 'QR код е автоматски доделен.'));
      } catch (error) {
        console.error('Failed to auto-assign QR code:', error);
        setCreationSuccess(t('profileCreator.success.created'));
      }
    } else {
      // Multiple QR codes - show selection modal, don't set success yet
      console.log(`Showing QR selection modal with ${qrCodes.length} available QR codes`);
      setShowQrSelection(true);
    }
  };

  const assignSelectedQrCode = async () => {
    if (!selectedQrCode) return;
    
    if (isWaitingForQrSelection) {
      // Pre-creation flow - create profile with selected QR code
      await createProfileWithQrSelection(selectedQrCode);
    } else {
      // Post-creation flow (existing logic for already created profiles)
      if (!createdProfileId) return;
      
      setIsAssigningQr(true);
      try {
        await fetchWithAuth('/qrcodes/assign-to-profile', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            qrCode: selectedQrCode,
            profileId: createdProfileId
          })
        });
        console.log('Assigned QR code:', selectedQrCode);
        setShowQrSelection(false);
        setCreationSuccess(t('profileCreator.success.created') + ' ' + t('profileCreator.qrCode.assigned', 'QR код е успешно доделен.'));
      } catch (error) {
        console.error('Failed to assign QR code:', error);
        setApiError(t('profileCreator.qrCode.assignError', 'Грешка при доделување на QR кодот.'));
      }
      setIsAssigningQr(false);
    }
  };

  const createProfileWithQrSelection = async (selectedQrCode = null) => {
    console.log('Creating profile with QR selection:', selectedQrCode);
    
    if (!pendingProfileData) {
      console.error('No pending profile data');
      return;
    }

    setIsSubmitting(true);
    setShowQrSelection(false);
    setIsWaitingForQrSelection(false);

    try {
      // Create the profile
      console.log('Creating profile with payload:', pendingProfileData.profilePayload);
      const profileResponse = await fetchWithAuth('/profiles', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(pendingProfileData.profilePayload)
      });
      console.log('Profile creation response:', profileResponse);
      const resultProfileId = profileResponse.stringId || profileResponse.id;
      console.log('Profile created successfully:', profileResponse);
      setCreatedProfileId(resultProfileId);
      
      // Dispatch event to notify other components
      window.dispatchEvent(new CustomEvent('profileUpdated', { detail: { profileId: resultProfileId } }));

      // Upload images if any
      const newImageFiles = pendingProfileData.imageFiles || [];
      if (newImageFiles.length > 0) {
        console.log(`Uploading ${newImageFiles.length} images...`);
        setUploadProgress({ current: 0, total: newImageFiles.length });
        let allImagesUploadedSuccessfully = true;
        const uploadedImageDetails = [];

        for (let i = 0; i < newImageFiles.length; i++) {
          const file = newImageFiles[i];
          const imageFormData = new FormData();
          imageFormData.append('file', file);
          setUploadProgress({ current: i + 1, total: newImageFiles.length });

          try {
            const imageResponse = await fetchWithAuth(`/profiles/${resultProfileId}/images`, {
              method: 'POST',
              body: imageFormData,
              headers: {}
            });
            uploadedImageDetails.push({
              ...imageResponse,
              originalIndex: i // Track original index for main image setting
            });
            console.log(`Image ${file.name} uploaded successfully with ID: ${imageResponse.id}`);
          } catch (imageUploadError) {
            console.error(`Failed to upload image ${file.name}:`, imageUploadError);
            allImagesUploadedSuccessfully = false;
            const errorMessage = imageUploadError.response?.data?.message || imageUploadError.message || `Error uploading ${file.name}`;
            setApiError(prev => prev + `\nFailed to upload ${file.name}: ${errorMessage}`);
          }
        }
        setUploadProgress({ current: 0, total: 0 });

        // Set the selected main image if uploads were successful
        const mainImageIndex = pendingProfileData.localMainImageIndex !== undefined ? pendingProfileData.localMainImageIndex : 0;
        console.log('Creation QR mode: Main image index from pending data:', mainImageIndex);
        
        if (allImagesUploadedSuccessfully && uploadedImageDetails.length > 0 && mainImageIndex !== 0) {
          try {
            console.log('Creation mode: Setting main image at index:', mainImageIndex);
            const targetImageDetail = uploadedImageDetails.find(detail => detail.originalIndex === mainImageIndex);
            
            if (targetImageDetail && targetImageDetail.id) {
              console.log(`Setting uploaded image with ID ${targetImageDetail.id} as main profile picture`);
              
              await fetchWithAuth(`/profiles/${resultProfileId}/images/${targetImageDetail.id}/set-main`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                }
              });
              console.log('Successfully set main profile picture in creation mode');
            } else {
              console.warn('Could not find uploaded image details for main image index:', mainImageIndex);
            }
          } catch (mainImageError) {
            console.error('Failed to set main profile picture in creation mode:', mainImageError);
            // Don't fail the entire process for this - it's not critical
          }
        } else {
          console.log('Using default main image (first uploaded) in creation mode');
        }

        if (!allImagesUploadedSuccessfully) {
          setCreationSuccess(t('profileCreator.success.created') + ' ' + t('profileCreator.error.someImagesFailed'));
        }
      }

      // Assign QR code if selected
      if (selectedQrCode && selectedQrCode !== "__no_assignment__") {
        try {
          console.log('Assigning QR code:', selectedQrCode, 'to profile:', resultProfileId);
          await fetchWithAuth('/qrcodes/assign-to-profile', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              qrCode: selectedQrCode,
              profileId: resultProfileId
            })
          });
          console.log('Successfully assigned QR code:', selectedQrCode);
          setCreationSuccess(t('profileCreator.success.created') + ' ' + t('profileCreator.qrCode.assigned', 'QR код е успешно доделен.'));
        } catch (error) {
          console.error('Failed to assign QR code:', error);
          // Don't let QR assignment failure break the entire flow
          setCreationSuccess(t('profileCreator.success.created') + ' (' + t('profileCreator.qrCode.notAssigned', 'QR код не е доделен') + ')');
        }
      } else {
        setCreationSuccess(t('profileCreator.success.created'));
      }

    } catch (error) {
      console.error('Profile creation failed:', error);
      if (error.response?.status === 403) {
        setQuotaExceeded(true);
        setApiError(t('profileCreator.quota.exceeded', 'Немате кредити за нов меморијален профил. Избришете постоечки или купете нов.'));
      } else {
        const errorMessage = error.response?.data?.message || error.message || t('profileCreator.error.generic');
        setApiError(errorMessage);
      }
    }

    setPendingProfileData(null);
    setIsSubmitting(false);
  };

  const cancelProfileCreation = () => {
    setShowQrSelection(false);
    setIsWaitingForQrSelection(false);
    setPendingProfileData(null);
    setSelectedQrCode('');
    setIsSubmitting(false);
    console.log('User cancelled profile creation');
    // Navigate to user profile page
    const lang = location.pathname.split('/')[1] || 'mk';
    navigate(`/${lang}/my-profile`, { replace: true });
  };

  const skipQrAssignment = () => {
    createProfileWithQrSelection(null);
  };

  const fetchProfileQrCode = async (profileId, userQrCodes = null) => {
    try {
      // Use provided QR codes or fetch them if not provided
      const qrCodes = userQrCodes || allUserQrCodes;
      
      // Find QR code assigned to this specific profile using numeric ID
      // Since QR codes still use numeric profileId, we use the stored numeric ID
      const profileQrCode = qrCodes.find(qr => qr.assigned && qr.profileId === numericProfileId);
      
      if (profileQrCode) {
        setCurrentQrCode(profileQrCode.code);
      } else {
        setCurrentQrCode(null);
      }
    } catch (error) {
      console.error('Failed to fetch profile QR code:', error);
      setCurrentQrCode(null);
    }
  };

  const openQrChangeModal = async () => {
    // Always fetch fresh QR codes before opening the modal
    await fetchAllUserQrCodes();
    setShowQrChangeModal(true);
  };

  const changeQrCode = async () => {
    if (!selectedQrCode || !profileId || !numericProfileId) return;
    
    setIsAssigningQr(true);
    try {
      // For regular users, always use assign-to-profile endpoint
      // The backend will handle unassigning from previous profile if needed
      // Use numeric profile ID since backend still expects numeric IDs
      await fetchWithAuth('/qrcodes/assign-to-profile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          qrCode: selectedQrCode,
          profileId: numericProfileId
        })
      });
      console.log('Assigned QR code to profile:', selectedQrCode);
      
      
      // Refresh both the QR code list and profile QR code information
      const updatedQrCodes = await fetchAllUserQrCodes();
      await fetchProfileQrCode(profileId, updatedQrCodes);
      
      setShowQrChangeModal(false);
      setSelectedQrCode('');
    } catch (error) {
      console.error('Failed to change QR code:', error);
      setApiError('Грешка при промена на QR кодот: ' + (error.message || 'Непозната грешка'));
    }
    setIsAssigningQr(false);
  };

  const closeQrChangeModal = () => {
    setShowQrChangeModal(false);
    setSelectedQrCode('');
  };

  const assignQrCodeToCurrentProfile = async (qrCode) => {
    if (!qrCode || !profileId || !numericProfileId) return;
    
    setIsAssigningQr(true);
    try {
      // For regular users, always use assign-to-profile endpoint
      // The backend will handle unassigning from previous profile if needed
      // Use numeric profile ID since backend still expects numeric IDs
      await fetchWithAuth('/qrcodes/assign-to-profile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          qrCode: qrCode,
          profileId: numericProfileId
        })
      });
      console.log('Assigned QR code to current profile:', qrCode);
      
      // Refresh both the QR code list and profile QR code information
      const updatedQrCodes = await fetchAllUserQrCodes();
      await fetchProfileQrCode(profileId, updatedQrCodes);
      
    } catch (error) {
      console.error('Failed to assign QR code:', error);
      setApiError('Грешка при доделување на QR кодот: ' + (error.message || 'Непозната грешка'));
    }
    setIsAssigningQr(false);
  };

  const unassignQrCodeFromProfile = async (qrCode) => {
    if (!qrCode || !profileId) return;
    

    setIsAssigningQr(true);
    try {
      const response = await fetchWithAuth(`/qrcodes/unassign-from-profile/${qrCode}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      console.log('Unassigned QR code from profile:', qrCode);
      
      // Refresh both the QR code list and profile QR code information
      const updatedQrCodes = await fetchAllUserQrCodes();
      await fetchProfileQrCode(profileId, updatedQrCodes);
      
    } catch (error) {
      console.error('Failed to unassign QR code:', error);
      
      if (error.message.includes('Unauthorized') || error.message.includes('401')) {
        setApiError('Сесијата е истечена. Ве молиме најавете се повторно.');
      } else {
        setApiError('Грешка при отстранување на QR кодот: ' + (error.message || 'Непозната грешка'));
      }
    }
    setIsAssigningQr(false);
  };

  const fetchProfileData = async () => {
    if (!isEditMode) return;
    
    setIsLoadingProfile(true);
    setAuthError(null);
    setIsUnauthorized(false);
    
    try {
      // First get current user data for authorization check
      const currentUserData = await fetchWithAuth('/users/me');
      
      // Then get profile data
      const profileData = await fetchWithAuth(`/profiles/${profileId}`);
      
      // Check authorization - user must own the profile or be an admin
      const userRole = localStorage.getItem('userRole');
      const currentUserId = currentUserData?.stringId || currentUserData?.id;
      
      // Debug logging to see what's happening
      console.log('=== AUTHORIZATION DEBUG ===');
      console.log('currentUserData:', currentUserData);
      console.log('currentUserId:', currentUserId);
      console.log('userRole:', userRole);
      console.log('profileData.userId:', profileData.userId);
      console.log('profileData.userId type:', typeof profileData.userId);
      console.log('currentUserId type:', typeof currentUserId);
      console.log('Are they equal?', profileData.userId === currentUserId);
      console.log('==========================');
      
      if (userRole !== 'ADMIN' && profileData.userId !== currentUserId) {
        console.log('AUTHORIZATION FAILED: userId mismatch');
        setIsUnauthorized(true);
        setAuthError(t('profileEdit.error.unauthorized', 'You are not authorized to edit this profile.'));
        return;
      }
      
      console.log('AUTHORIZATION PASSED: User can edit this profile');
      
      // Store the numeric profile ID for QR code matching (since QR codes still use numeric profile IDs)
      setNumericProfileId(profileData.id);
      console.log('Stored numeric profile ID for QR matching:', profileData.id);
      
      // Handle coordinates conversion from object to string format
      let coordinatesString = '';
      if (profileData.coordinates) {
        if (typeof profileData.coordinates === 'string') {
          // Check if it's a JSON string that needs parsing
          if (profileData.coordinates.startsWith('{') && profileData.coordinates.endsWith('}')) {
            try {
              const parsedCoords = JSON.parse(profileData.coordinates);
              if (parsedCoords.lat && parsedCoords.lng) {
                coordinatesString = `${parsedCoords.lat}, ${parsedCoords.lng}`;
              } else {
                // Not a coordinate object, use as-is
                coordinatesString = profileData.coordinates;
              }
            } catch (e) {
              // Not valid JSON, use as-is
              coordinatesString = profileData.coordinates;
            }
          } else {
            // Already in string format
            coordinatesString = profileData.coordinates;
          }
        } else if (typeof profileData.coordinates === 'object' && profileData.coordinates.lat && profileData.coordinates.lng) {
          // Convert from object format to string format
          coordinatesString = `${profileData.coordinates.lat}, ${profileData.coordinates.lng}`;
        }
      }
      
      // Populate form with existing profile data
      setFormData({
        name: profileData.name || '',
        introduction: profileData.introduction || '',
        lifeStory: profileData.lifeStory || '',
        birthDate: profileData.birthDate || '',
        birthPlace: profileData.birthPlace || '',
        deathDate: profileData.deathDate || '',
        deathPlace: profileData.deathPlace || '',
        restingPlaceName: profileData.restingPlaceName || '',
        restingPlaceCity: profileData.restingPlaceCity || '',
        restingPlaceCountry: profileData.restingPlaceCountry || '',
        coordinates: coordinatesString,
        defaultLanguage: profileData.defaultLanguage || 'ENGLISH',
        isAllowed: profileData.isAllowed !== undefined ? profileData.isAllowed : true, // Admin field
        gallery: [] // We'll handle existing images separately
      });

      // Handle existing images if any
      if (profileData.uploadedImages && profileData.uploadedImages.length > 0) {
        // Debug the uploaded images data
        console.log('=== LOADED IMAGES DEBUG ===');
        console.log('profileData.uploadedImages:', profileData.uploadedImages);
        profileData.uploadedImages.forEach((img, index) => {
          console.log(`Image ${index}:`, {
            id: img.id,
            filename: img.filename,
            isMainProfilePicture: img.isMainProfilePicture,
            url: img.url
          });
        });
        console.log('==========================');

        // Store full image objects for tracking deletions
        setExistingImages(profileData.uploadedImages);

        // Set preview URLs for display
        const existingImageUrls = profileData.uploadedImages.map(img => img.url);
        setPreviewUrls(prev => ({
          ...prev,
          gallery: existingImageUrls
        }));

        // Initialize localMainImageIndex based on which existing image is main
        const mainImageIndex = profileData.uploadedImages.findIndex(img => img.isMainProfilePicture);
        if (mainImageIndex >= 0) {
          setLocalMainImageIndex(mainImageIndex);
          console.log(`Initialized localMainImageIndex to ${mainImageIndex} based on existing main image`);
        } else {
          // Fallback to first image if no main image found
          setLocalMainImageIndex(0);
          console.log('No main image found, defaulting localMainImageIndex to 0');
        }

        console.log(`Loaded ${profileData.uploadedImages.length} existing images for editing`);
      } else {
        // Clear existing images state if no images
        setExistingImages([]);
      }

      // Fetch QR code information for edit mode
      const qrCodes = await fetchAllUserQrCodes();
      await fetchProfileQrCode(profileId, qrCodes);

    } catch (error) {
      console.error('Failed to fetch profile data:', error);
      if (error.response?.status === 404) {
        setAuthError(t('profileEdit.error.notFound', 'Profile not found.'));
        setIsUnauthorized(true);
      } else if (error.response?.status === 403) {
        setAuthError(t('profileEdit.error.unauthorized', 'You are not authorized to edit this profile.'));
        setIsUnauthorized(true);
      } else {
        setApiError(t('profileEdit.error.fetchFailed', 'Failed to fetch profile data.'));
      }
    } finally {
      setIsLoadingProfile(false);
    }
  };

  useEffect(() => {
    if (!hasValidAuthentication()) {
      saveIntendedDestination(location.pathname + location.search);
      navigate(`/${getLanguageFromPath(location.pathname)}/login`, { replace: true });
    } else {
      fetchUserData();
      fetchProfileData();
    }
  }, [navigate, location.pathname, location.search, isEditMode, profileId]);

  // Force re-render when allUserQrCodes changes to ensure UI updates
  useEffect(() => {
    console.log('QR codes state updated:', allUserQrCodes.length, 'codes');
  }, [allUserQrCodes]);

  const validateField = (name, value, currentFormData = formData) => {
    switch (name) {
      case 'name':
        return value.trim() === '' ? t('profileCreator.validation.required') : '';
      case 'birthDate':
        if (value && value.trim() !== '') {
          // Support three formats: DD.MM.YYYY (full), MM.YYYY (month+year), YYYY (year only)
          const fullDatePattern = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;
          const monthYearPattern = /^(\d{1,2})\.(\d{4})$/;
          const yearPattern = /^(\d{4})$/;
          
          if (fullDatePattern.test(value)) {
            // Full date validation
            const [day, month, year] = value.split('.').map(Number);
            if (year < 1000 || year > new Date().getFullYear()) {
              return t('profileCreator.validation.invalidYear');
            }
            if (month < 1 || month > 12) {
              return t('profileCreator.validation.invalidMonth');
            }
            if (day < 1 || day > 31) {
              return t('profileCreator.validation.invalidDay');
            }
          } else if (monthYearPattern.test(value)) {
            // Month and year validation
            const [month, year] = value.split('.').map(Number);
            if (year < 1000 || year > new Date().getFullYear()) {
              return t('profileCreator.validation.invalidYear');
            }
            if (month < 1 || month > 12) {
              return t('profileCreator.validation.invalidMonth');
            }
          } else if (yearPattern.test(value)) {
            // Year only validation
            const year = Number(value);
            if (year < 1000 || year > new Date().getFullYear()) {
              return t('profileCreator.validation.invalidYear');
            }
          } else {
            return t('profileCreator.validation.invalidDateFormat');
          }
        }
        return '';
      case 'deathDate':
        if (value && value.trim() !== '') {
          // Support three formats: DD.MM.YYYY (full), MM.YYYY (month+year), YYYY (year only)
          const fullDatePattern = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;
          const monthYearPattern = /^(\d{1,2})\.(\d{4})$/;
          const yearPattern = /^(\d{4})$/;
          
          let deathYear, deathMonth, deathDay;
          
          if (fullDatePattern.test(value)) {
            // Full date validation
            const [day, month, year] = value.split('.').map(Number);
            if (year < 1000 || year > new Date().getFullYear()) {
              return t('profileCreator.validation.invalidYear');
            }
            if (month < 1 || month > 12) {
              return t('profileCreator.validation.invalidMonth');
            }
            if (day < 1 || day > 31) {
              return t('profileCreator.validation.invalidDay');
            }
            deathYear = year;
            deathMonth = month;
            deathDay = day;
          } else if (monthYearPattern.test(value)) {
            // Month and year validation
            const [month, year] = value.split('.').map(Number);
            if (year < 1000 || year > new Date().getFullYear()) {
              return t('profileCreator.validation.invalidYear');
            }
            if (month < 1 || month > 12) {
              return t('profileCreator.validation.invalidMonth');
            }
            deathYear = year;
            deathMonth = month;
            deathDay = 1; // Use first day of month for comparison
          } else if (yearPattern.test(value)) {
            // Year only validation
            const year = Number(value);
            if (year < 1000 || year > new Date().getFullYear()) {
              return t('profileCreator.validation.invalidYear');
            }
            deathYear = year;
            deathMonth = 1; // Use January 1st for comparison
            deathDay = 1;
          } else {
            return t('profileCreator.validation.invalidDateFormat');
          }
          
          // Check if death date is after birth date
          const currentBirthDate = currentFormData.birthDate;
          if (currentBirthDate && currentBirthDate.trim() !== '') {
            const fullBirthDatePattern = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;
            const monthYearBirthPattern = /^(\d{1,2})\.(\d{4})$/;
            const yearBirthPattern = /^(\d{4})$/;
            
            let birthYear, birthMonth, birthDay;
            
            if (fullBirthDatePattern.test(currentBirthDate)) {
              const [bDay, bMonth, bYear] = currentBirthDate.split('.').map(Number);
              birthYear = bYear;
              birthMonth = bMonth;
              birthDay = bDay;
            } else if (monthYearBirthPattern.test(currentBirthDate)) {
              const [bMonth, bYear] = currentBirthDate.split('.').map(Number);
              birthYear = bYear;
              birthMonth = bMonth;
              birthDay = 1;
            } else if (yearBirthPattern.test(currentBirthDate)) {
              const bYear = Number(currentBirthDate);
              birthYear = bYear;
              birthMonth = 1;
              birthDay = 1;
            }
            
            if (birthYear !== undefined) {
              const birthDate = new Date(birthYear, birthMonth - 1, birthDay);
              const deathDate = new Date(deathYear, deathMonth - 1, deathDay);
              if (deathDate < birthDate) {
                return t('profileCreator.validation.deathDateBeforeBirth');
              }
            }
          }
        }
        return '';
      case 'coordinates':
        if (value && value.trim() !== '') {
          const coords = value.split(',').map(coord => coord.trim());
          if (coords.length !== 2) {
            return t('profileCreator.validation.invalidCoordinates');
          }
          const [lat, lng] = coords.map(Number);
          if (isNaN(lat) || lat < -90 || lat > 90 || isNaN(lng) || lng < -180 || lng > 180) {
            return t('profileCreator.validation.invalidCoordinates');
          }
        }
        return '';
      default:
        return '';
    }
  };

  const handleGpsConfirm = () => {
    if (!navigator.geolocation) {
      setGpsStatus('not_supported');
      return;
    }
    setGpsStatus('fetching');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const coordinatesString = `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
        setFormData(prev => ({ ...prev, coordinates: coordinatesString }));
        setGpsStatus('success');
        setTimeout(() => {
          setShowGpsModal(false);
          setGpsStatus('idle');
        }, 1500);
      },
      () => {
        setGpsStatus('error');
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Update form data first
    const updatedFormData = {
      ...formData,
      [name]: value
    };
    
    // Validate with the updated form data
    const fieldError = validateField(name, value, updatedFormData);
    
    setErrors(prev => ({
      ...prev,
      [name]: fieldError
    }));

    setFormData(updatedFormData);
  };

  const GALLERY_CAP = 100;

  const handleGalleryFiles = (files) => {
    // Count existing (already uploaded) + new (staged) images to find remaining capacity
    const currentCount = existingImages.length + formData.gallery.length;
    const remaining = GALLERY_CAP - currentCount;

    if (remaining <= 0) {
      setErrors(prev => ({
        ...prev,
        gallery: `Максимално ${GALLERY_CAP} слики се дозволени`
      }));
      return;
    }

    // Accept only as many files as fit within the cap — never block the whole batch
    const filesToAdd = files.slice(0, remaining);

    if (filesToAdd.length < files.length) {
      setErrors(prev => ({
        ...prev,
        gallery: `Додадени ${filesToAdd.length} слики (${files.length - filesToAdd.length} прескокнати – максимум ${GALLERY_CAP} вкупно)`
      }));
    } else {
      // Clear any previous gallery error if everything fit
      setErrors(prev => ({ ...prev, gallery: '' }));
    }

    const newPreviewUrls = filesToAdd.map(file => URL.createObjectURL(file));

    setFormData(prev => ({
      ...prev,
      gallery: [...prev.gallery, ...filesToAdd]
    }));

    setPreviewUrls(prev => ({
      ...prev,
      gallery: [...prev.gallery, ...newPreviewUrls]
    }));
  };

  const removeGalleryImage = (index) => {
    console.log('=== removeGalleryImage called ===');
    console.log('Index:', index);
    console.log('Total existing images:', existingImages.length);
    console.log('Total preview URLs:', previewUrls.gallery.length);
    
    const totalExistingImages = existingImages.length;
    
    if (index < totalExistingImages) {
      // Removing an existing image - use immediate delete in edit mode
      const imageToDelete = existingImages[index];
      console.log('Removing existing image:', imageToDelete);

      if (isEditMode && imageToDelete.id) {
        // Use immediate delete for real-time feedback
        handleImmediateDelete(imageToDelete.id, index);
        return; // Exit early since handleImmediateDelete handles UI updates
      } else {
        // Fallback: mark for deletion (for creation mode or edge cases)
        setImagesToDelete(prev => [...prev, imageToDelete]);
        setExistingImages(prev => prev.filter((_, i) => i !== index));
      }

      // Clear any error messages - deletion is now supported
      setApiError('');
    } else {
      // Removing a new image (File object)
      const newImageIndex = index - totalExistingImages;
      console.log('Removing new uploaded image at index:', newImageIndex);
      
      // Clear any previous error messages
      setApiError('');
      
      // Revoke object URL if it exists
      if (previewUrls.gallery[index] && previewUrls.gallery[index].startsWith('blob:')) {
        URL.revokeObjectURL(previewUrls.gallery[index]);
      }
      
      setFormData(prev => ({
        ...prev,
        gallery: prev.gallery.filter((_, i) => i !== newImageIndex)
      }));
    }
    
    // Only update UI state if not using immediate delete (which handles its own updates)
    if (!isEditMode || index >= totalExistingImages) {
      // Remove from preview URLs (for immediate UI feedback)
      setPreviewUrls(prev => ({
        ...prev,
        gallery: prev.gallery.filter((_, i) => i !== index)
      }));

      // Update localMainImageIndex for all modes when removing images
      if (index < localMainImageIndex) {
        // Image before the main image was removed, decrease main index
        setLocalMainImageIndex(prev => Math.max(0, prev - 1));
        console.log(`Updated localMainImageIndex: decreased by 1 due to removal before main image`);
      } else if (index === localMainImageIndex) {
        // The main image itself was removed, set to first remaining image
        setLocalMainImageIndex(0);
        console.log(`Updated localMainImageIndex: reset to 0 because main image was removed`);
      }
      // If index > localMainImageIndex, no change needed

      // If no images left, reset to 0
      const remainingImagesCount = previewUrls.gallery.length - 1; // -1 because we're removing one
      if (remainingImagesCount === 0) {
        setLocalMainImageIndex(0);
        console.log(`Updated localMainImageIndex: reset to 0 because no images remain`);
      }

      console.log(`Image at index ${index} removed from UI`);
    }
  };

  const setMainProfilePicture = async (imageId) => {
    if (!isEditMode) {
      console.log('Cannot set main profile picture in create mode');
      return;
    }

    try {
      console.log('Setting image', imageId, 'as main profile picture for profile', profileId);

      // Ensure we have a valid token before making the request
      const token = localStorage.getItem('jwtToken');
      if (!token) {
        console.error('No auth token available');
        toast.error(t('errors.unauthorized'));
        return;
      }

      const response = await fetchWithAuth(`/profiles/${profileId}/images/${imageId}/set-main`, {
        method: 'PUT',
      });

      if (!response) {
        console.error('No response received from server');
        toast.error(t('errors.serverError'));
        return;
      }

      console.log('Successfully set main profile picture:', response);

      // Update existing images to reflect the change
      setExistingImages(prev => prev.map(img => ({
        ...img,
        isMainProfilePicture: img.id === imageId
      })));

      // Force a reload of images to ensure we have the latest state
      try {
        await fetchProfileImages();
      } catch (error) {
        console.error('Failed to refresh images:', error);
      }

    } catch (error) {
      console.error('Failed to set main profile picture:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to set main profile picture';
      setApiError(errorMessage);
    }
  };

  // Handle setting main image during creation mode (for local images without IDs)
  const setLocalMainImage = (imageIndex) => {
    console.log('Setting local main image index to:', imageIndex);
    console.log('Previous localMainImageIndex was:', localMainImageIndex);
    setLocalMainImageIndex(imageIndex);
    console.log('Local main image index updated to:', imageIndex);
  };

  // Unified function to handle main image setting for both modes
  const handleSetMainImage = (imageId, imageIndex) => {
    console.log('=== MAIN IMAGE SELECTION ===');
    console.log('handleSetMainImage called with:', { imageId, imageIndex, isEditMode });
    console.log('Previous localMainImageIndex:', localMainImageIndex);

    if (isEditMode && imageId) {
      // In edit mode with existing images, use the server function immediately
      console.log('Using server function for edit mode (existing image)');
      setMainProfilePicture(imageId);
      // Also update local state for visual consistency
      setLocalMainImageIndex(imageIndex);
    } else {
      // In creation mode or for new images, use local state tracking
      console.log('Using local state tracking, setting index:', imageIndex);
      setLocalMainImageIndex(imageIndex);
      console.log('Updated localMainImageIndex to:', imageIndex);
    }
    console.log('============================');
  };

  // Immediate delete function for edit mode
  const handleImmediateDelete = async (imageId, imageIndex) => {
    if (!isEditMode || !imageId || !profileId) {
      console.log('Cannot immediately delete: not in edit mode or missing IDs');
      return;
    }

    try {
      console.log(`Immediately deleting image ${imageId} from profile ${profileId}`);

      // Call the backend delete API
      await fetchWithAuth(`/profiles/${profileId}/images/${imageId}`, {
        method: 'DELETE'
      });

      console.log(`Successfully deleted image with ID: ${imageId}`);

      // Remove from existing images
      setExistingImages(prev => prev.filter(img => img.id !== imageId));

      // Remove from preview URLs
      setPreviewUrls(prev => ({
        ...prev,
        gallery: prev.gallery.filter((_, i) => i !== imageIndex)
      }));

      // Refresh the profile data to get updated main image status
      await fetchProfileData();

      console.log('Image deleted and profile data refreshed');

    } catch (error) {
      console.error('Failed to delete image immediately:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete image';
      setApiError(`Failed to delete image: ${errorMessage}`);
    }
  };

  useEffect(() => {
    return () => {
      // Revoke all object URLs in the gallery
      if (previewUrls.gallery && Array.isArray(previewUrls.gallery)) {
        previewUrls.gallery.forEach(url => {
          if (url && typeof url.startsWith === 'function' && url.startsWith('blob:')) {
            URL.revokeObjectURL(url);
          }
        });
      }
      // Revoke other potential single image previews if they exist and are blob URLs
      Object.keys(previewUrls).forEach(key => {
        if (key !== 'gallery' && previewUrls[key] && typeof previewUrls[key].startsWith === 'function' && previewUrls[key].startsWith('blob:')) {
          URL.revokeObjectURL(previewUrls[key]);
        }
      });
    };
  }, [previewUrls]); // Depend on the whole previewUrls object

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Form submission started with data:', formData);
    
    const newErrors = {};
    let hasErrors = false;

    // Validate all form fields with current form data
    Object.keys(formData).forEach(key => {
      if (key === 'gallery') return; // Skip gallery validation here
      
      const fieldError = validateField(key, formData[key], formData);
      if (fieldError) {
        newErrors[key] = fieldError;
        hasErrors = true;
        console.log(`Validation error for field ${key}:`, fieldError);
      }
    });

    // Special case: name is required
    if (!formData.name || formData.name.trim() === '') {
      newErrors.name = t('profileCreator.validation.required');
      hasErrors = true;
      console.log('Name field is required but empty');
    }

    // Special case: defaultLanguage is required
    if (!formData.defaultLanguage || formData.defaultLanguage.trim() === '') {
      newErrors.defaultLanguage = t('profileCreator.validation.required');
      hasErrors = true;
      console.log('Default language field is required but empty');
    }

    if (hasErrors) {
      console.log('Form has validation errors:', newErrors);
      setErrors(newErrors);
      return;
    }

    console.log('Form validation passed, submitting...');
    setIsSubmitting(true);
    setApiError('');
    setCreationSuccess(null);

    const profilePayload = {
      name: formData.name,
      introduction: formData.introduction,
      lifeStory: formData.lifeStory,
      birthDate: formData.birthDate,
      birthPlace: formData.birthPlace,
      deathDate: formData.deathDate,
      deathPlace: formData.deathPlace,
      restingPlaceName: formData.restingPlaceName,
      restingPlaceCity: formData.restingPlaceCity,
      restingPlaceCountry: formData.restingPlaceCountry,
      coordinates: formData.coordinates,
      defaultLanguage: formData.defaultLanguage,
    };

    // Add admin-only fields if user is admin
    if (localStorage.getItem('userRole') === 'ADMIN') {
      profilePayload.isAllowed = formData.isAllowed;
    }

    // For new profiles, check QR codes first before creating profile
    if (!isEditMode) {
      const unusedQrCodes = await fetchUnusedQrCodes();
      console.log(`Fetched unused QR codes for new profile: ${unusedQrCodes.length} available:`, unusedQrCodes.map(qr => qr.code));
      if (unusedQrCodes.length >= 2) {
        // Multiple unused QR codes available - show selection first
        console.log(`Found ${unusedQrCodes.length} unused QR codes, showing selection before profile creation`);
        setPendingProfileData({ 
          profilePayload, 
          imageFiles: formData.gallery.filter(item => item instanceof File),
          localMainImageIndex: localMainImageIndex
        });
        setIsWaitingForQrSelection(true);
        // Refresh QR codes once more right before showing modal to ensure accuracy
        await fetchUnusedQrCodes();
        setShowQrSelection(true);
        setIsSubmitting(false);
        return;
      }
    }

    console.log('Sending profile payload:', profilePayload);

    try {
      let profileResponse;
      let resultProfileId;

      if (isEditMode) {
        // Update existing profile
        profileResponse = await fetchWithAuth(`/profiles/${profileId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(profilePayload)
        });
        resultProfileId = profileId;
        console.log('Profile updated successfully:', profileResponse);
        setCreationSuccess(t('profileEdit.success', 'Profile updated successfully!'));
      } else {
        // Create new profile
        profileResponse = await fetchWithAuth('/profiles', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(profilePayload)
        });
        resultProfileId = profileResponse.stringId || profileResponse.id;
        console.log('Profile created successfully:', profileResponse);
        // Don't set success message yet for new profiles - wait for QR code assignment
        if (isEditMode) {
          setCreationSuccess(t('profileEdit.success', 'Profile updated successfully!'));
        }
      }
      
      setCreatedProfileId(resultProfileId);
      
      // Dispatch event to notify other components that a profile was created/updated
      window.dispatchEvent(new CustomEvent('profileUpdated', { detail: { profileId: resultProfileId } }));

      // 2. Delete removed existing images (only in edit mode)
      if (isEditMode && imagesToDelete.length > 0) {
        console.log(`Deleting ${imagesToDelete.length} existing images...`);
        console.log('Images to delete:', imagesToDelete);
        
        let deletionErrors = [];
        
        for (const imageToDelete of imagesToDelete) {
          try {
            console.log(`Deleting image ${imageToDelete.id} from profile ${resultProfileId}`);
            await fetchWithAuth(`/profiles/${resultProfileId}/images/${imageToDelete.id}`, {
              method: 'DELETE'
            });
            console.log(`Successfully deleted image with ID: ${imageToDelete.id}`);
          } catch (deleteError) {
            console.error(`Failed to delete image ${imageToDelete.id}:`, deleteError);
            
            // Check specific error types
            if (deleteError.message.includes('403') || deleteError.message.includes('Forbidden')) {
              deletionErrors.push(`Unauthorized to delete image ${imageToDelete.id}`);
            } else if (deleteError.message.includes('401') || deleteError.message.includes('Unauthorized')) {
              deletionErrors.push(`Authentication failed when deleting image ${imageToDelete.id}`);
            } else {
              const errorMessage = deleteError.message || `Error deleting image ${imageToDelete.id}`;
              deletionErrors.push(errorMessage);
            }
          }
        }
        
        // Show any deletion errors
        if (deletionErrors.length > 0) {
          setApiError(`Some images could not be deleted:\n${deletionErrors.join('\n')}`);
        }
        
        // Clear the deletion list after processing
        setImagesToDelete([]);
      }

      // 3. Upload Images if any (only for new files in gallery)
      const newImageFiles = formData.gallery.filter(item => item instanceof File);
      if (resultProfileId && newImageFiles.length > 0) {
        console.log(`Uploading ${newImageFiles.length} images...`);
        setCreationSuccess(prev => prev + ' ' + t('profileCreator.info.uploadingImages'));
        setUploadProgress({ current: 0, total: newImageFiles.length });
        let allImagesUploadedSuccessfully = true;
        const uploadedImageDetails = [];

        for (let i = 0; i < newImageFiles.length; i++) {
          const file = newImageFiles[i];
          const imageFormData = new FormData();
          imageFormData.append('file', file);
          setUploadProgress({ current: i + 1, total: newImageFiles.length });

          try {
            const imageResponse = await fetchWithAuth(`/profiles/${resultProfileId}/images`, {
              method: 'POST',
              body: imageFormData,
              headers: {} // Don't set Content-Type for FormData - let browser set it with boundary
            });
            uploadedImageDetails.push({
              ...imageResponse,
              originalIndex: i // Track original index for main image setting
            });
            console.log(`Image ${file.name} uploaded successfully`);
          } catch (imageUploadError) {
            console.error(`Failed to upload image ${file.name}:`, imageUploadError);
            allImagesUploadedSuccessfully = false;
            const errorMessage = imageUploadError.response?.data?.message || imageUploadError.message || `Error uploading ${file.name}`;
            setApiError(prev => prev + `\nFailed to upload ${file.name}: ${errorMessage}`);
          }
        }
        setUploadProgress({ current: 0, total: 0 });

        // Set the locally marked image as main if uploads were successful
        if (allImagesUploadedSuccessfully && uploadedImageDetails.length > 0) {
          try {
            // Calculate the correct index for new images
            // localMainImageIndex is the position in the combined array (existing + new)
            // We need to find the corresponding new image index
            const existingImagesCount = existingImages.length;
            
            let targetImageDetail = null;
            let targetDescription = '';
            
            // Check if the selected main image is among the newly uploaded images
            if (localMainImageIndex >= existingImagesCount) {
              // Main image is among new uploads
              const newImageIndex = localMainImageIndex - existingImagesCount;
              targetImageDetail = uploadedImageDetails.find(detail => detail.originalIndex === newImageIndex);
              targetDescription = `new image at index ${newImageIndex} (local index ${localMainImageIndex})`;
              
              console.log('Setting newly uploaded image as main:', {
                localMainImageIndex,
                existingImagesCount,
                newImageIndex,
                targetImageId: targetImageDetail?.id
              });
            } else if (!isEditMode && existingImagesCount === 0) {
              // Pure creation mode - localMainImageIndex directly refers to new images
              targetImageDetail = uploadedImageDetails.find(detail => detail.originalIndex === localMainImageIndex);
              targetDescription = `new image at index ${localMainImageIndex}`;
            } else {
              console.log('Main image is among existing images, not setting during upload');
            }
            
            if (targetImageDetail && targetImageDetail.id) {
              console.log(`Setting uploaded image with ID ${targetImageDetail.id} (${targetDescription}) as main profile picture`);
              
              await fetchWithAuth(`/profiles/${resultProfileId}/images/${targetImageDetail.id}/set-main`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                }
              });
              console.log('Successfully set main profile picture after upload');
            } else if (localMainImageIndex >= existingImagesCount) {
              console.warn('Could not find uploaded image details for target image');
            }
          } catch (mainImageError) {
            console.error('Failed to set main profile picture after upload:', mainImageError);
            // Don't fail the entire process for this - it's not critical
          }
        }

        if (allImagesUploadedSuccessfully) {
          // For edit mode, show success immediately with image upload message
          if (isEditMode) {
            setCreationSuccess(prev => prev + ' ' + t('profileCreator.success.allImagesUploaded'));
          }
          
          // Handle QR code assignment for new profiles only
          if (!isEditMode) {
            // Don't set success message yet - let QR selection handle it
            await handleQrCodeSelection(resultProfileId);
          }
        } else {
          // For failed image uploads, show error message
          if (isEditMode) {
            setCreationSuccess(prev => prev + ' ' + t('profileCreator.error.someImagesFailed'));
          } else {
            // For new profiles, still need to handle QR selection even with image failures
            setCreationSuccess(t('profileCreator.success.created') + ' ' + t('profileCreator.error.someImagesFailed'));
            await handleQrCodeSelection(resultProfileId);
          }
        }
      } else if (resultProfileId) {
        // Profile created/updated, no new images to upload
        // Handle QR code assignment for new profiles only
        if (!isEditMode) {
          await handleQrCodeSelection(resultProfileId);
        }
      }

    } catch (error) {
      console.error(`Profile ${isEditMode ? 'update' : 'creation'} failed:`, error);
      if (!isEditMode && error.response?.status === 403) {
        // Profile quota exceeded (only for create mode)
        setQuotaExceeded(true);
        setApiError('Немате кредити за нов меморијален профил. Избришете постоечки или купете нов.');
      } else {
        const errorMessage = error.response?.data?.message || error.message || 
          (isEditMode ? t('profileEdit.error.updateFailed') : t('profileCreator.error.generic'));
        setApiError(errorMessage);
      }
      setCreationSuccess(null); // Clear any partial success message
    }

    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 pt-6">

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          
          {quotaExceeded && (
            <div className="bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-2xl p-8 mb-8 shadow-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center">
                    <i className="fas fa-exclamation-triangle text-white text-xl"></i>
                  </div>
                </div>
                <div className="ml-6 flex-1">
                  <h3 className="text-xl font-semibold text-red-800 mb-3">
                    Немате кредити за нов меморијален профил.
                  </h3>
                  <p className="text-red-700 mb-6 text-lg">
                    Достигнавте го максималниот број на меморијални профили што можете да ги креирате ({userData?.maxProfiles}).
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Link 
                      to={`/${location.pathname.split('/')[1] || 'mk'}/my-profile`}
                      className="inline-flex items-center px-6 py-3 border border-red-300 text-sm font-medium rounded-xl text-red-700 bg-white hover:bg-red-50 transition-all duration-200 shadow-md hover:shadow-lg"
                    >
                      <i className="fas fa-trash mr-3"></i>
                      Избришете постоечки меморијален профил
                    </Link>
                    <Link 
                      to={`/${location.pathname.split('/')[1] || 'mk'}/products`}
                      className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-md hover:shadow-lg"
                    >
                      <i className="fas fa-shopping-cart mr-3"></i>
                      Купете нов меморијален профил
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {creationSuccess ? (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-8 text-center shadow-lg">
              <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="fas fa-check-circle text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-semibold text-green-800 mb-4" data-testid="profile-creation-success-message">
                {isEditMode ? t('profileEdit.success', 'Profile updated successfully!') : creationSuccess}
              </h3>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to={`/${location.pathname.split('/')[1] || 'mk'}/profile/${createdProfileId}`} 
                  className="inline-flex items-center px-8 py-4 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 transition-all duration-200 shadow-md hover:shadow-lg"
                  data-testid="profile-creator-view-profile-link"
                >
                  <i className="fas fa-eye mr-3"></i>
                  {t('profileCreator.viewProfileLink')}
                </Link>
              </div>
            </div>
          ) : (
            <>
              {isLoadingProfile && (
                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">{t('common.loading', 'Loading...')}</p>
                </div>
              )}

              {!isLoadingProfile && isUnauthorized && (
                <div className="bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-2xl p-8 text-center shadow-lg">
                  <div className="w-20 h-20 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="fas fa-lock text-white text-3xl"></i>
                  </div>
                  <h3 className="text-2xl font-semibold text-red-800 mb-4">
                    {t('profileEdit.error.accessDenied', 'Access Denied')}
                  </h3>
                  <p className="text-red-700 text-lg mb-8">
                    {authError}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link 
                      to={`/${location.pathname.split('/')[1] || 'mk'}/my-profile`}
                      className="inline-flex items-center px-8 py-4 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-md hover:shadow-lg"
                    >
                      <i className="fas fa-user mr-3"></i>
                      {t('profileEdit.goToMyProfiles', 'Go to My Profiles')}
                    </Link>
                    <Link 
                      to={`/${location.pathname.split('/')[1] || 'mk'}`}
                      className="inline-flex items-center px-8 py-4 border border-gray-300 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 transition-all duration-200 shadow-md hover:shadow-lg"
                    >
                      <i className="fas fa-home mr-3"></i>
                      {t('common.goHome', 'Go Home')}
                    </Link>
                  </div>
                </div>
              )}

              {!isLoadingProfile && !isUnauthorized && (
                <>
                  {apiError && (
                    <div className="bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-2xl p-6 mb-8 shadow-lg">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
                            <i className="fas fa-exclamation-circle text-white"></i>
                          </div>
                        </div>
                        <div className="ml-4">
                          <p className="text-red-800 font-medium">{apiError}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                    {/* Title Section with Divider */}
                    <section className="p-8 border-b border-gray-200">
                      <div className="text-center">
                        <h1 className="text-3xl font-bold text-gray-900 flex items-center justify-center">
                          <i className={`fas ${isEditMode ? 'fa-edit' : 'fa-heart'} text-blue-500 text-2xl mr-4`}></i>
                          {isEditMode ? t('profileEdit.title', 'Edit Profile') : t('profileCreator.title')}
                        </h1>
                        <div className="mt-6 w-24 h-0.5 bg-gradient-to-r from-blue-500 to-indigo-500 mx-auto rounded-full"></div>
                      </div>
                    </section>

                    {/* Basic Information Section */}
                    <section className="p-8 border-b border-gray-100">
                      <div className="flex items-center mb-8">
                        <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0">
                          <i className="fas fa-user-circle text-white text-xl"></i>
                        </div>
                        <div className="ml-4 flex-1">
                          <h2 className="text-2xl font-bold text-gray-900">
                            {t('profileCreator.basicInfo', 'Основни Информации')}
                          </h2>
                          <p className="text-gray-600">Основните информации за мемориалниот профил</p>
                        </div>
                      </div>
                      
                      <div className="space-y-8">
                        <div>
                          <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-3">
                            {t('profileCreator.fields.name.label', 'Цело Име')} <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center justify-center pointer-events-none">
                              <i className="fas fa-user text-gray-400"></i>
                            </div>
                            <input
                              type="text"
                              id="name"
                              name="name"
                              value={formData.name}
                              onChange={handleChange}
                              className={`block w-full pl-12 pr-4 py-4 text-lg border ${
                                errors.name ? 'border-red-300 bg-red-50' : 'border-gray-300'
                              } rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200`}
                              placeholder="Внесете го целото име"
                              data-testid="profile-creator-name-input"
                              required
                            />
                          </div>
                          {errors.name && (
                            <p className="mt-3 text-sm text-red-600 flex items-center">
                              <i className="fas fa-exclamation-circle mr-2"></i>
                              {errors.name}
                            </p>
                          )}
                        </div>

                        <div>
                          <label htmlFor="introduction" className="block text-sm font-semibold text-gray-700 mb-3">
                            {t('profileCreator.fields.introduction', 'Вовед (Кратка Биографија)')}
                          </label>
                          <textarea
                            id="introduction"
                            name="introduction"
                            value={formData.introduction}
                            onChange={handleChange}
                            rows="4"
                            className="block w-full px-4 py-4 text-lg border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none"
                            placeholder="Кратка биографија или опис..."
                          />
                        </div>

                        <div>
                          <label htmlFor="defaultLanguage" className="block text-sm font-semibold text-gray-700 mb-3">
                            {t('profileCreator.fields.defaultLanguage.label', 'Default Language')} <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center justify-center pointer-events-none">
                              <i className="fas fa-globe text-gray-400"></i>
                            </div>
                            <select
                              id="defaultLanguage"
                              name="defaultLanguage"
                              value={formData.defaultLanguage}
                              onChange={handleChange}
                              className={`block w-full pl-12 pr-4 py-4 text-lg border ${
                                errors.defaultLanguage ? 'border-red-300 bg-red-50' : 'border-gray-300'
                              } rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 appearance-none bg-white`}
                              data-testid="profile-creator-defaultlanguage-select"
                              required
                            >
                              <option value="">{t('profileCreator.fields.defaultLanguage.placeholder', 'Select Language')}</option>
                              <option value="MACEDONIAN">{t('languages.macedonian', 'Macedonian')}</option>
                              <option value="ALBANIAN">{t('languages.albanian', 'Albanian')}</option>
                              <option value="TURKISH">{t('languages.turkish', 'Turkish')}</option>
                              <option value="ENGLISH">{t('languages.english', 'English')}</option>
                              <option value="GERMAN">{t('languages.german', 'German')}</option>
                              <option value="SERBIAN">{t('languages.serbian', 'Serbian')}</option>
                              <option value="CROATIAN">{t('languages.croatian', 'Croatian')}</option>
                            </select>
                            <div className="absolute inset-y-0 right-0 pr-4 flex items-center justify-center pointer-events-none">
                              <i className="fas fa-chevron-down text-gray-400"></i>
                            </div>
                          </div>
                          {errors.defaultLanguage && (
                            <p className="mt-3 text-sm text-red-600 flex items-center">
                              <i className="fas fa-exclamation-circle mr-2"></i>
                              {errors.defaultLanguage}
                            </p>
                          )}
                          <p className="mt-2 text-sm text-gray-600">
                            {t('profileCreator.fields.defaultLanguage.help', 'Choose the primary language for this memorial profile')}
                          </p>
                        </div>
                      </div>
                    </section>

                    {/* Admin Controls Section - Only visible to admins */}
                    {localStorage.getItem('userRole') === 'ADMIN' && (
                      <section className="p-8 border-b border-gray-100 bg-gradient-to-r from-amber-50 to-yellow-50">
                        <div className="flex items-center mb-8">
                          <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-orange-600 rounded-xl flex items-center justify-center flex-shrink-0">
                            <i className="fas fa-shield-alt text-white text-xl"></i>
                          </div>
                          <div className="ml-4 flex-1">
                            <h2 className="text-2xl font-bold text-gray-900">
                              {t('profileCreator.adminControls', 'Admin Controls')}
                            </h2>
                            <p className="text-orange-700 font-medium">Admin-only visibility controls for this profile</p>
                          </div>
                        </div>
                        
                        <div className="bg-white p-6 rounded-xl border border-orange-200 shadow-sm">
                          <label htmlFor="isAllowed" className="block text-sm font-semibold text-gray-700 mb-3">
                            <i className="fas fa-eye text-orange-600 mr-2"></i>
                            {t('profileCreator.fields.isAllowed.label', 'Profile Visibility Status')}
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center justify-center pointer-events-none">
                              <i className={`fas ${formData.isAllowed ? 'fa-check-circle text-green-500' : 'fa-ban text-red-500'}`}></i>
                            </div>
                            <select
                              id="isAllowed"
                              name="isAllowed"
                              value={formData.isAllowed}
                              onChange={(e) => setFormData(prev => ({ ...prev, isAllowed: e.target.value === 'true' }))}
                              className="block w-full pl-12 pr-4 py-4 text-lg border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 appearance-none bg-white"
                              data-testid="profile-creator-isallowed-select"
                            >
                              <option value={true}>{t('profileCreator.fields.isAllowed.allowed', '✓ Allowed - Profile is visible to public')}</option>
                              <option value={false}>{t('profileCreator.fields.isAllowed.blocked', '✗ Blocked - Profile is hidden from public')}</option>
                            </select>
                            <div className="absolute inset-y-0 right-0 pr-4 flex items-center justify-center pointer-events-none">
                              <i className="fas fa-chevron-down text-gray-400"></i>
                            </div>
                          </div>
                          <div className={`mt-3 p-3 rounded-lg text-sm ${formData.isAllowed ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                            <i className={`fas ${formData.isAllowed ? 'fa-info-circle' : 'fa-exclamation-triangle'} mr-2`}></i>
                            {formData.isAllowed 
                              ? t('profileCreator.fields.isAllowed.allowedHelp', 'This profile will be visible to all users who access the /profile route.')
                              : t('profileCreator.fields.isAllowed.blockedHelp', 'This profile is blocked and will return a 404 error when accessed by non-admin users. Use this to immediately hide profiles with inappropriate content.')
                            }
                          </div>
                        </div>
                      </section>
                    )}

                    {/* Dates Section */}
                    <section className="p-8 border-b border-gray-100">
                      <div className="flex items-center mb-8">
                        <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0">
                          <i className="fas fa-calendar-alt text-white text-xl"></i>
                        </div>
                        <div className="ml-4 flex-1">
                          <h2 className="text-2xl font-bold text-gray-900">
                            {t('profileCreator.datesInfo', 'Важни Датуми')}
                          </h2>
                          <p className="text-gray-600">{t('profileCreator.datesInfoSubtitle', 'Информации за раѓање и смрт')}</p>
                        </div>
                      </div>
                      
                      <div className="space-y-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                          <DatePickerField
                            label={t('profileCreator.fields.birthDate.label', 'Датум на Раѓање')}
                            value={formData.birthDate}
                            onChange={(value) => {
                              setFormData({ ...formData, birthDate: value });
                              const error = validateField('birthDate', value);
                              setErrors({ ...errors, birthDate: error });
                            }}
                            error={errors.birthDate}
                            placeholder={t('profileCreator.placeholders.dateFormat', 'ДД.ММ.ГГГГ, ММ.ГГГГ или ГГГГ')}
                            testId="profile-creator-birthdate-input"
                            id="birthDate"
                            name="birthDate"
                          />
                          <div>
                            <label htmlFor="birthPlace" className="block text-sm font-semibold text-gray-700 mb-3">
                              {t('profileCreator.fields.birthPlace.label', 'Место на Раѓање')}
                            </label>
                            <div className="relative">
                              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <i className="fas fa-map-marker-alt text-gray-400"></i>
                              </div>
                              <input
                                type="text"
                                id="birthPlace"
                                name="birthPlace"
                                value={formData.birthPlace}
                                onChange={handleChange}
                                className="block w-full pl-12 pr-4 py-4 text-lg border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                placeholder="Место на раѓање"
                                data-testid="profile-creator-birthplace-input"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                          <DatePickerField
                            label={t('profileCreator.fields.deathDate', 'Датум на Смрт')}
                            value={formData.deathDate}
                            onChange={(value) => {
                              setFormData({ ...formData, deathDate: value });
                              const error = validateField('deathDate', value, { ...formData, deathDate: value });
                              setErrors({ ...errors, deathDate: error });
                            }}
                            error={errors.deathDate}
                            placeholder={t('profileCreator.placeholders.dateFormat', 'ДД.ММ.ГГГГ, ММ.ГГГГ или ГГГГ')}
                            testId="profile-creator-deathdate-input"
                            id="deathDate"
                            name="deathDate"
                          />
                          <div>
                            <label htmlFor="deathPlace" className="block text-sm font-semibold text-gray-700 mb-3">
                              {t('profileCreator.fields.deathPlace', 'Место на Смрт')}
                            </label>
                            <div className="relative">
                              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <i className="fas fa-map-marker-alt text-gray-400"></i>
                              </div>
                              <input
                                type="text"
                                id="deathPlace"
                                name="deathPlace"
                                value={formData.deathPlace}
                                onChange={handleChange}
                                className="block w-full pl-12 pr-4 py-4 text-lg border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                placeholder="Место на смрт"
                                data-testid="profile-creator-deathplace-input"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </section>

                    {/* Life Story Section */}
                    <section className="p-8 border-b border-gray-100">
                      <div className="flex items-center mb-8">
                        <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl flex items-center justify-center flex-shrink-0">
                          <i className="fas fa-book text-white text-xl"></i>
                        </div>
                        <div className="ml-4 flex-1">
                          <h2 className="text-2xl font-bold text-gray-900">
                            {t('profileCreator.lifeStory', 'Животна Приказна')}
                          </h2>
                          <p className="text-gray-600">Споделете ја животната приказна</p>
                        </div>
                      </div>
                      
                      <div>
                        <label htmlFor="lifeStory" className="block text-sm font-semibold text-gray-700 mb-3">
                          {t('profileCreator.fields.lifeStory.label', 'Детална Животна Приказна')}
                        </label>
                        <textarea
                          id="lifeStory"
                          name="lifeStory"
                          value={formData.lifeStory}
                          onChange={handleChange}
                          rows="8"
                          className="block w-full px-4 py-4 text-lg border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 resize-none"
                          placeholder="Опишете ја животната приказна, достигнувања, спомени..."
                          data-testid="profile-creator-lifestory-textarea"
                        />
                      </div>
                    </section>

                    {/* Resting Place Section */}
                    <section className="p-8 border-b border-gray-100">
                      <div className="flex items-center mb-8">
                        <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-teal-600 rounded-xl flex items-center justify-center flex-shrink-0">
                          <i className="fas fa-map-marker-alt text-white text-xl"></i>
                        </div>
                        <div className="ml-4 flex-1">
                          <h2 className="text-2xl font-bold text-gray-900">
                            {t('profileCreator.restingPlace', 'Место на Почивање')}
                          </h2>
                          <p className="text-gray-600">Информации за местото на почивање</p>
                        </div>
                      </div>
                      
                      <div className="space-y-8">
                        <div>
                          <label htmlFor="restingPlaceName" className="block text-sm font-semibold text-gray-700 mb-3">
                            {t('profileCreator.fields.restingPlaceName.label', 'Име на Местото на Почивање')}
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center justify-center pointer-events-none">
                              <i className="fas fa-landmark text-gray-400"></i>
                            </div>
                            <input
                              type="text"
                              id="restingPlaceName"
                              name="restingPlaceName"
                              value={formData.restingPlaceName}
                              onChange={handleChange}
                              className="block w-full pl-12 pr-4 py-4 text-lg border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                              placeholder="Име на гробиштето или локацијата"
                              data-testid="profile-creator-restingplacename-input"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                          <div>
                            <label htmlFor="restingPlaceCity" className="block text-sm font-semibold text-gray-700 mb-3">
                              {t('profileCreator.fields.restingPlaceCity.label', 'Град')}
                            </label>
                            <div className="relative">
                              <div className="absolute inset-y-0 left-0 pl-4 flex items-center justify-center pointer-events-none">
                                <i className="fas fa-city text-gray-400"></i>
                              </div>
                              <input
                                type="text"
                                id="restingPlaceCity"
                                name="restingPlaceCity"
                                value={formData.restingPlaceCity}
                                onChange={handleChange}
                                className="block w-full pl-12 pr-4 py-4 text-lg border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                                placeholder="Град"
                                data-testid="profile-creator-restingplacecity-input"
                              />
                            </div>
                          </div>
                          <div>
                            <label htmlFor="restingPlaceCountry" className="block text-sm font-semibold text-gray-700 mb-3">
                              {t('profileCreator.fields.restingPlaceCountry', 'Држава')}
                            </label>
                            <div className="relative">
                              <div className="absolute inset-y-0 left-0 pl-4 flex items-center justify-center pointer-events-none">
                                <i className="fas fa-flag text-gray-400"></i>
                              </div>
                              <input
                                type="text"
                                id="restingPlaceCountry"
                                name="restingPlaceCountry"
                                value={formData.restingPlaceCountry}
                                onChange={handleChange}
                                className="block w-full pl-12 pr-4 py-4 text-lg border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                                placeholder="Држава"
                                data-testid="profile-creator-restingplacecountry-input"
                              />
                            </div>
                          </div>
                        </div>

                        <div>
                          <label htmlFor="coordinates" className="block text-sm font-semibold text-gray-700 mb-3">
                            {t('profileCreator.fields.coordinates', 'Координати (Географска Ширина, Должина)')}
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center justify-center pointer-events-none">
                              <i className="fas fa-map-marker text-gray-400"></i>
                            </div>
                            <input
                              type="text"
                              id="coordinates"
                              name="coordinates"
                              value={formData.coordinates}
                              onChange={handleChange}
                              placeholder="41.9981, 21.4254"
                              className={`block w-full pl-12 pr-4 py-4 text-lg border ${
                                errors.coordinates ? 'border-red-300 bg-red-50' : 'border-gray-300'
                              } rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200`}
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => { setGpsStatus('idle'); setShowGpsModal(true); }}
                            className="mt-3 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
                          >
                            <i className="fas fa-location-crosshairs"></i>
                            {t('profileCreator.fields.coordinates.gpsButton', 'Користи ја мојата локација')}
                          </button>
                          {errors.coordinates && (
                            <p className="mt-3 text-sm text-red-600 flex items-center">
                              <i className="fas fa-exclamation-circle mr-2"></i>
                              {errors.coordinates}
                            </p>
                          )}
                          <p className="mt-2 text-sm text-gray-600">
                            {t('profileCreator.fields.coordinates.helpText', 'Прецизна локација од гробот. Повеќе информации како може да ја најдете видете на')}{' '}
                            <a 
                              href="/coordinates-guide" 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-indigo-600 hover:text-indigo-800 font-semibold underline"
                            >
                              {t('profileCreator.fields.coordinates.guideLink', 'следниов линк')}
                            </a>
                          </p>
                        </div>
                      </div>
                    </section>

                    {/* QR Code Section - Only in Edit Mode */}
                    {isEditMode && (
                      <section className="p-8 border-b border-gray-100" data-testid="qr-code-section">
                        <div className="flex items-center justify-between mb-8">
                          <div className="flex items-center">
                            <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                              <i className="fas fa-qrcode text-white text-xl"></i>
                            </div>
                            <div className="ml-4 flex-1">
                              <h2 className="text-2xl font-bold text-gray-900" data-testid="qr-section-title">
                                QR Кодови
                              </h2>
                              <p className="text-gray-600">Управувајте со QR кодовите за овој профил</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={openQrChangeModal}
                            className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors"
                            data-testid="assign-qr-button"
                          >
                            <i className="fas fa-plus mr-2"></i>
                            Додели QR код
                          </button>
                        </div>
                        
                        <div className="bg-gray-50 rounded-xl p-6" key={`qr-section-${allUserQrCodes.length}-${profileId}`} data-testid="qr-display-section">
                          {(() => {
                            // Filter QR codes using numeric profile ID since QR codes still use numeric profileId
                            const assignedQrCodes = allUserQrCodes.filter(qr => qr.assigned && qr.profileId === numericProfileId);
                            
                            if (assignedQrCodes.length === 0) {
                              return (
                                <div className="text-center py-8" data-testid="no-qr-assigned">
                                  <i className="fas fa-qrcode text-gray-400 text-4xl mb-4"></i>
                                  <h4 className="font-semibold text-gray-700 mb-2">Нема доделени QR кодови</h4>
                                  <p className="text-gray-500 mb-4">Овој профил нема доделени QR кодови</p>
                                </div>
                              );
                            }
                            
                            return (
                              <div className="space-y-4" data-testid="assigned-qr-list">
                                <h4 className="font-semibold text-gray-900 mb-4">Доделени QR кодови:</h4>
                                {assignedQrCodes.map((qr) => (
                                  <div key={qr.id} className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 hover:border-green-300 transition-colors" data-testid={`assigned-qr-${qr.code}`}>
                                    <div className="flex items-center">
                                      <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center mr-4">
                                        <i className="fas fa-qrcode text-white"></i>
                                      </div>
                                      <div>
                                        <p className="font-mono text-lg font-semibold text-gray-900">{qr.code}</p>
                                        <p className="text-sm text-green-600">Активен на овој профил</p>
                                      </div>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                        <i className="fas fa-check mr-1"></i>
                                        Активен
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => unassignQrCodeFromProfile(qr.code)}
                                        className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                                        disabled={isAssigningQr}
                                        title="Отстрани QR код"
                                        data-testid={`remove-qr-${qr.code}`}
                                      >
                                        {isAssigningQr ? (
                                          <i className="fas fa-spinner fa-spin"></i>
                                        ) : (
                                          <i className="fas fa-times"></i>
                                        )}
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            );
                          })()}
                        </div>
                      </section>
                    )}

                    {/* Images Section */}
                    <section className="p-8">
                      <div className="flex items-center mb-8">
                        <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-600 rounded-xl flex items-center justify-center flex-shrink-0">
                          <i className="fas fa-images text-white text-xl"></i>
                        </div>
                        <div className="ml-4 flex-1">
                          <h2 className="text-2xl font-bold text-gray-900">
                            {t('profileCreator.images', 'Слики')}
                          </h2>
                          <p className="text-gray-600">Прикачете слики за мемориалниот профил</p>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-4">
                          {t('profileCreator.fields.gallerySingleUpload', 'Прикачи Слики (до 100)')}
                        </label>
                        <FileUpload
                          onFilesSelected={handleGalleryFiles}
                          maxFiles={GALLERY_CAP}
                          acceptedFileTypes={{
                            'image/jpeg': ['.jpg', '.jpeg'],
                            'image/png': ['.png']
                          }}
                          maxSize={10 * 1024 * 1024}
                        />
                        
                        {previewUrls.gallery && previewUrls.gallery.length > 0 && (
                          <div className="mt-8">
                            <h4 className="text-lg font-semibold text-gray-700 mb-2">Преглед на слики:</h4>
                            <p className="text-sm text-gray-600 mb-4">
                              <span className="font-medium">Кликнете на "Set as Main" за да изберете главна слика.</span>
                              {!isEditMode && <span> Главната слика ќе биде зачувана кога ќе го креирате профилот.</span>}
                              {isEditMode && <span> Промените се применуваат веднаш.</span>}
                            </p>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                              {previewUrls.gallery.map((url, index) => {
                                // Find corresponding existing image for this preview URL
                                const existingImage = index < existingImages.length ? existingImages[index] : null;

                                // Determine if this image is marked as main
                                // In all cases, only the image at localMainImageIndex should show as main
                                const isMain = (index === localMainImageIndex);

                                if (index === 0) {
                                  console.log(`Visual feedback update - Image ${index}:`, {
                                    isEditMode,
                                    existingImageId: existingImage?.id,
                                    localMainImageIndex,
                                    index,
                                    isMain,
                                    totalImages: previewUrls.gallery.length
                                  });
                                }

                                return (
                                  <div key={index} className="relative group">
                                    <ImagePreview
                                      imageUrl={url}
                                      onRemove={() => removeGalleryImage(index)}
                                      altText={`Слика ${index + 1}`}
                                      imageId={existingImage?.id}
                                      isMainProfilePicture={isMain}
                                      onSetMainProfilePicture={handleSetMainImage}
                                      showMainProfileButton={true} // Always show the button now
                                      imageIndex={index}
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </section>

                    {/* Form Actions */}
                    <div className="bg-gray-50 px-8 py-6 border-t border-gray-100">
                      <div className="flex flex-col sm:flex-row justify-end space-y-4 sm:space-y-0 sm:space-x-4">
                        <button
                          type="button"
                          onClick={() => {
                            const lang = location.pathname.split('/')[1] || 'mk';
                            navigate(`/${lang}/my-profile`);
                          }}
                          className="inline-flex items-center justify-center px-8 py-4 border border-gray-300 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200 shadow-md hover:shadow-lg"
                        >
                          <i className="fas fa-times mr-3"></i>
                          {t('profileCreator.buttons.cancel', 'Откажи')}
                        </button>
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="inline-flex items-center justify-center px-8 py-4 border border-transparent text-sm font-medium rounded-xl shadow-md text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-lg"
                          data-testid="profile-creator-submit-button"
                        >
                          {isSubmitting ? (
                            <>
                              <i className="fas fa-spinner fa-spin mr-3"></i>
                              {uploadProgress.total > 0
                                ? `Слика ${uploadProgress.current} / ${uploadProgress.total}...`
                                : (isEditMode 
                                    ? t('profileEdit.saving', 'Saving...') 
                                    : t('profileCreator.buttons.submitting', 'Се креира...')
                                  )
                              }
                            </>
                          ) : (
                            <>
                              <i className={`fas ${isEditMode ? 'fa-save' : 'fa-heart'} mr-3`}></i>
                              {isEditMode 
                                ? t('profileEdit.save', 'Save Changes')
                                : t('profileCreator.buttons.submit', 'Креирај Меморијален Профил')
                              }
                            </>
                          )}
                        </button>
                      </div>

                      {/* Warning banner shown while submitting */}
                      {isSubmitting && (
                        <div className="mt-4 flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 px-5 py-4 text-amber-800">
                          <i className="fas fa-triangle-exclamation mt-0.5 text-amber-500 text-lg flex-shrink-0"></i>
                          <div className="text-sm leading-snug">
                            <span className="font-semibold block mb-0.5">
                              {uploadProgress.total > 0
                                ? `Се прикачуваат слики (${uploadProgress.current} / ${uploadProgress.total})…`
                                : 'Се зачувува…'}
                            </span>
                            Ве молиме <strong>не го затворајте</strong> овој прозорец и не одите на друга страница додека не заврши процесот.
                          </div>
                        </div>
                      )}
                    </div>
                  </form>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* QR Code Selection Modal */}
      {showQrSelection && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="qr-selection-modal">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-qrcode text-white text-2xl"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2" data-testid="qr-modal-title">
                  Избери QR Код
                </h3>
                <p className="text-gray-600">
                  Имате {availableQrCodes.length} QR кодови. Изберете еден за овој профил:
                </p>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Ваши QR кодови: <span className="text-red-500">*</span>
                </label>
                <div className="max-h-64 overflow-y-auto space-y-2 border border-gray-300 rounded-lg p-3">
                  <div className="space-y-2">
                    <label className="flex items-center p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="radio"
                        name="qr-selection"
                        value="__no_assignment__"
                        checked={selectedQrCode === "__no_assignment__"}
                        onChange={(e) => setSelectedQrCode(e.target.value)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <div className="ml-3 flex-1">
                        <div className="flex items-center">
                          <i className="fas fa-times-circle text-gray-400 mr-2"></i>
                          <span className="text-sm font-medium text-gray-900">Не доделувај QR код засега</span>
                        </div>
                      </div>
                    </label>
                    {availableQrCodes.map((qr) => (
                      <label key={qr.id} className="flex items-center p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                        <input
                          type="radio"
                          name="qr-selection"
                          value={qr.code}
                          checked={selectedQrCode === qr.code}
                          onChange={(e) => setSelectedQrCode(e.target.value)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <div className="ml-3 flex-1">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <div className={`w-8 h-8 rounded flex items-center justify-center mr-3 ${
                                qr.assigned ? 'bg-orange-100' : 'bg-gray-100'
                              }`}>
                                <i className={`fas fa-qrcode ${qr.assigned ? 'text-orange-600' : 'text-gray-600'}`}></i>
                              </div>
                              <div>
                                <span className="text-sm font-medium text-gray-900">{qr.code}</span>
                                {qr.assigned && (
                                  <p className="text-xs text-orange-600">
                                    Моментално доделен на: {qr.profileName}
                                  </p>
                                )}
                              </div>
                            </div>
                            {qr.assigned && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                Доделен
                              </span>
                            )}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                {isWaitingForQrSelection && (
                  <button
                    onClick={cancelProfileCreation}
                    className="flex-1 px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors"
                  >
                    <i className="fas fa-times mr-2"></i>
                    {t('profileCreator.buttons.cancelCreation', 'Откажи Креирање')}
                  </button>
                )}
                <button
                  onClick={() => {
                    if (selectedQrCode === "__no_assignment__") {
                      skipQrAssignment();
                    } else {
                      assignSelectedQrCode();
                    }
                  }}
                  disabled={!selectedQrCode || isAssigningQr}
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isAssigningQr ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Се обработува...
                    </>
                  ) : selectedQrCode === "__no_assignment__" ? (
                    'Продолжи без QR код'
                  ) : (
                    'Додели QR Код'
                  )}
                </button>
              </div>
            </div>
                     </div>
         </div>
       )}

      {/* QR Code Change Modal - For Edit Mode */}
      {showGpsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              {gpsStatus === 'success' ? (
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="fas fa-check text-white text-2xl"></i>
                  </div>
                  <p className="text-lg font-semibold text-gray-900">
                    {t('profileCreator.gpsLocationModal.success', 'Локацијата е успешно земена!')}
                  </p>
                </div>
              ) : (
                <>
                  <div className="text-center mb-5">
                    <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="fas fa-location-crosshairs text-white text-2xl"></i>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {t('profileCreator.gpsLocationModal.title', 'Земи GPS Локација')}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {t('profileCreator.gpsLocationModal.description', 'Ова ќе ги земе GPS координатите на вашата тековна локација.')}
                    </p>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                    <div className="flex gap-3">
                      <i className="fas fa-triangle-exclamation text-amber-500 mt-0.5 flex-shrink-0"></i>
                      <p className="text-sm text-amber-800 font-medium">
                        {t('profileCreator.gpsLocationModal.warning', 'Треба физички да стоите кај надгробниот споменик во моментот кога ќе ја земете локацијата.')}
                      </p>
                    </div>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-5">
                    <div className="flex gap-3">
                      <i className="fas fa-circle-info text-blue-500 mt-0.5 flex-shrink-0"></i>
                      <p className="text-sm text-blue-800">
                        {t('profileCreator.gpsLocationModal.permissionNote', 'Вашиот прелистувач или уред ќе прикаже прозорец за дозвола за пристап до вашата локација — ве молиме притиснете Дозволи.')}
                      </p>
                    </div>
                  </div>
                  {gpsStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4">
                      <p className="text-sm text-red-700 flex items-center gap-2">
                        <i className="fas fa-circle-xmark"></i>
                        {t('profileCreator.gpsLocationModal.error', 'Не може да се земе локацијата. Обидете се повторно или внесете ги координатите рачно.')}
                      </p>
                    </div>
                  )}
                  {gpsStatus === 'not_supported' && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4">
                      <p className="text-sm text-red-700 flex items-center gap-2">
                        <i className="fas fa-circle-xmark"></i>
                        {t('profileCreator.gpsLocationModal.notSupported', 'GPS локацијата не е поддржана на вашиот уред.')}
                      </p>
                    </div>
                  )}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      type="button"
                      onClick={() => { setShowGpsModal(false); setGpsStatus('idle'); }}
                      disabled={gpsStatus === 'fetching'}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
                    >
                      {t('profileCreator.gpsLocationModal.cancel', 'Откажи')}
                    </button>
                    <button
                      type="button"
                      onClick={handleGpsConfirm}
                      disabled={gpsStatus === 'fetching'}
                      className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {gpsStatus === 'fetching' ? (
                        <><i className="fas fa-spinner fa-spin mr-2"></i>{t('profileCreator.gpsLocationModal.fetching', 'Земање на локацијата...')}</>
                      ) : (
                        <><i className="fas fa-location-crosshairs mr-2"></i>{t('profileCreator.gpsLocationModal.confirm', 'Јас сум кај гробот — земи ја мојата локација')}</>
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showQrChangeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="qr-change-modal">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-indigo-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-exchange-alt text-white text-2xl"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2" data-testid="qr-change-modal-title">
                  {currentQrCode ? 'Промени QR Код' : 'Додели QR Код'}
                </h3>
                <p className="text-gray-600">
                  {availableQrCodes.length > 0 
                    ? `Имате ${availableQrCodes.length} QR кодови. Изберете еден:`
                    : 'Немате QR кодови за доделување.'
                  }
                </p>
              </div>

              {availableQrCodes.length > 0 ? (
                <>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Ваши QR кодови:
                    </label>
                    <div className="max-h-64 overflow-y-auto space-y-2 border border-gray-300 rounded-lg p-3">
                      <div className="space-y-2">
                        {availableQrCodes.map((qr) => (
                          <label key={qr.id} className="flex items-center p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                            <input
                              type="radio"
                              name="qr-change-selection"
                              value={qr.code}
                              checked={selectedQrCode === qr.code}
                              onChange={(e) => setSelectedQrCode(e.target.value)}
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                            />
                            <div className="ml-3 flex-1">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                  <div className={`w-8 h-8 rounded flex items-center justify-center mr-3 ${
                                    qr.assigned && qr.profileId === numericProfileId
                                      ? 'bg-green-100'
                                      : qr.assigned 
                                        ? 'bg-orange-100' 
                                        : 'bg-gray-100'
                                  }`}>
                                    <i className={`fas fa-qrcode ${
                                      qr.assigned && qr.profileId === numericProfileId
                                        ? 'text-green-600'
                                        : qr.assigned 
                                          ? 'text-orange-600' 
                                          : 'text-gray-600'
                                    }`}></i>
                                  </div>
                                  <div>
                                    <span className="text-sm font-medium text-gray-900">{qr.code}</span>
                                    {qr.assigned && qr.profileId === numericProfileId && (
                                      <p className="text-xs text-green-600">
                                        Тековно доделен на овој профил
                                      </p>
                                    )}
                                    {qr.assigned && qr.profileId !== numericProfileId && (
                                      <p className="text-xs text-orange-600">
                                        Моментално доделен на: {qr.profileName}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                {qr.assigned && qr.profileId === numericProfileId && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Активен
                                  </span>
                                )}
                                {qr.assigned && qr.profileId !== numericProfileId && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                    Доделен
                                  </span>
                                )}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={closeQrChangeModal}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      {t('common.cancel', 'Откажи')}
                    </button>
                    <button
                      onClick={changeQrCode}
                      disabled={!selectedQrCode || isAssigningQr}
                      className="flex-1 px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isAssigningQr ? (
                        <>
                          <i className="fas fa-spinner fa-spin mr-2"></i>
                          Се доделува...
                        </>
                      ) : (
                        currentQrCode ? 'Промени QR Код' : 'Додели QR Код'
                      )}
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-center">
                  <button
                    onClick={closeQrChangeModal}
                    className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    Затвори
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
     </div>
   );
 };

export default ProfileCreator;
